import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, Loader2, CheckCircle2, Copy, Tag, Sparkles, Gift } from "lucide-react";
import { SERVICES } from "@/lib/index";
import { supabase } from "@/integrations/supabase/client";

interface OrderFormProps {
  initialDiscountCode?: string;
  wheelCode?: string;
  wheelDiscount?: string;
  preselectedService?: string;
  onSuccess?: () => void;
}

export function OrderForm({ 
  initialDiscountCode = '', 
  wheelCode = "", 
  wheelDiscount = "",
  preselectedService: _pre,
  onSuccess
}: OrderFormProps = {}) {
  const [discountCode, setDiscountCode] = useState(wheelCode || initialDiscountCode);
  const [discountInfo, setDiscountInfo] = useState<{ valid: boolean; discount: string; message: string } | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  
  // Wheel discount calculation
  const discountPct = wheelDiscount ? ({ 
    "10%": 10, 
    "20%": 20, 
    "30%": 30, 
    "50%": 50, 
    "PITANJE": 0, 
    "ANALIZA": 100 
  }[wheelDiscount] ?? 0) : 0;
  
  const priceMap: Record<string, number> = {
    "natal-chart": 2000,
    "natal-predictions": 3300,
    "predictions-only": 1500,
    "synastry": 3000,
    "three-questions": 1500,
    "five-questions": 2000,
    "ten-questions": 3000,
  };
const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    message: '',
    // Nova polja
    maritalStatus: '',
    employmentStatus: '',
    // Polja za partnera (Sinastija)
    partnerFirstName: '',
    partnerLastName: '',
    partnerBirthDate: '',
    partnerBirthTime: '',
    partnerBirthPlace: '',
    // Pitanja
    question1: '',
    question2: '',
    question3: '',
    question4: '',
    question5: '',
    question6: '',
    question7: '',
    question8: '',
    question9: '',
    question10: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [error, setError] = useState('');

  // Funkcija za prikaz alert-a sa novom cenom
  const showPriceAlert = (discountAmount: string) => {
    // Service mapping (isti kao u backend-u)
    const serviceMap: Record<string, { title: string; priceNumeric: number }> = {
      'natal-chart': { title: 'Natalna Karta', priceNumeric: 2000 },
      'natal-predictions': { title: 'Natalna Karta + Predikcije', priceNumeric: 3300 },
      'predictions-only': { title: 'Samo Predikcije', priceNumeric: 1500 },
      'synastry': { title: 'Sinastija', priceNumeric: 2400 },
      'three-questions': { title: 'Tri Pitanja', priceNumeric: 900 },
      'five-questions': { title: '5 Pitanja', priceNumeric: 1400 },
      'ten-questions': { title: '10 Pitanja', priceNumeric: 2700 }
    };

    const selectedService = serviceMap[formData.service];
    
    if (!selectedService) {
      alert('🎉 Čestitamo!\n\nVaš discount kod je primenjen!\n\nMolimo odaberite uslugu i popunite formu, zatim kliknite "Pošalji Porudžbinu".');
      return;
    }

    const originalPrice = selectedService.priceNumeric;
    let finalPrice = originalPrice;
    let message = '';

    if (discountAmount === 'FREE') {
      finalPrice = 0;
      message = `🎉 ČESTITAMO!\n\n✨ Osvojili ste BESPLATNU analizu!\n\n📋 ${selectedService.title}\n💰 Originalna cena: ${originalPrice.toLocaleString('sr-RS')} RSD\n🎁 Nova cena: BESPLATNO!\n\n👉 Idite na "Pošalji Porudžbinu" da završite proces!`;
    } else {
      const discountPercent = parseInt(discountAmount.replace('%', ''));
      finalPrice = originalPrice * (1 - discountPercent / 100);
      message = `🎉 ČESTITAMO!\n\n✨ Dobili ste ${discountAmount} popusta!\n\n📋 ${selectedService.title}\n💰 Originalna cena: ${originalPrice.toLocaleString('sr-RS')} RSD\n🎁 Nova cena: ${finalPrice.toLocaleString('sr-RS')} RSD\n💵 Ušteda: ${(originalPrice - finalPrice).toLocaleString('sr-RS')} RSD\n\n👉 Idite na "Pošalji Porudžbinu" da završite proces!`;
    }

    alert(message);
  };

  // Funkcija za validaciju discount koda (useCallback da izbegnemo dependency probleme)
  const validateDiscountCode = useCallback(async (code: string) => {
    console.log('Validating discount code:', code);
    
    if (!code || code.trim() === '') {
      console.log('Code is empty');
      setDiscountInfo(null);
      return;
    }

    setIsValidatingCode(true);
    
    try {
      console.log('Querying database for code:', code.toUpperCase());
      
      // Proveri da li kod postoji u bazi
      const { data, error } = await supabase
        .from('wheel_spins_2026_03_20')
        .select('*')
        .eq('discount_code', code.toUpperCase())
        .single();

      console.log('Query result:', { data, error });

      if (error || !data) {
        console.log('Code not found or error');
        setDiscountInfo({
          valid: false,
          discount: '0%',
          message: 'Nevažeći kod. Molimo proverite i pokušajte ponovo.'
        });
      } else if (data.is_used) {
        console.log('Code already used');
        setDiscountInfo({
          valid: false,
          discount: '0%',
          message: 'Ovaj kod je već iskorišćen.'
        });
      } else {
        console.log('Code is valid!', data.prize_type);
        setDiscountInfo({
          valid: true,
          discount: data.prize_type,
          message: `Odlično! Dobili ste ${data.prize_type === 'FREE' ? 'BESPLATNU analizu' : data.prize_type + ' popusta'}! 🎉`
        });
        
        // Prikaži alert sa novom cenom
        showPriceAlert(data.prize_type);
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setDiscountInfo({
        valid: false,
        discount: '0%',
        message: 'Greška pri validaciji koda.'
      });
    } finally {
      setIsValidatingCode(false);
    }
  }, []); // Nema dependencies jer koristi samo state setters

  // Automatski validira kod kada se komponenta učita sa kodom
  useEffect(() => {
    if (initialDiscountCode) {
      setDiscountCode(initialDiscountCode);
      validateDiscountCode(initialDiscountCode);
    }
  }, [initialDiscountCode, validateDiscountCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted with discount:', { discountCode, discountInfo });
    setError('');
    setIsSubmitting(true);

try {
// Validacija obaveznih polja
      if (!formData.firstName || !formData.lastName || !formData.email || 
          !formData.service || !formData.birthDate || !formData.birthTime || 
          !formData.birthPlace || !formData.maritalStatus || !formData.employmentStatus) {
        setError('Molimo popunite sva obavezna polja');
        setIsSubmitting(false);
        return;
      }

// Validacija polja partnera za Sinastiju
      if (formData.service === 'synastry') {
        if (!formData.partnerFirstName || !formData.partnerLastName || 
            !formData.partnerBirthDate || !formData.partnerBirthTime || 
            !formData.partnerBirthPlace) {
          setError('Molimo popunite sve podatke o partneru za sinastiju');
          setIsSubmitting(false);
          return;
        }
      }

      // Validacija pitanja za usluge sa pitanjima
      const numQuestions = getNumberOfQuestions();
      if (numQuestions > 0) {
        for (let i = 1; i <= numQuestions; i++) {
          if (!formData[`question${i}` as keyof typeof formData]) {
            setError(`Molimo unesite pitanje ${i}`);
            setIsSubmitting(false);
            return;
          }
        }
      }

// Poziv Edge funkcije
      const { data, error: functionError } = await supabase.functions.invoke('create_order_2026_03_19', {
        body: {
          ...formData,
          discountCode: discountInfo?.valid ? discountCode : '',
          discountAmount: discountInfo?.valid ? discountInfo.discount : '0%'
        }
      });

      if (functionError) {
        throw functionError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

// Uspešno poslato
      setPaymentInfo(data.paymentInfo);
      setShowSuccess(true);
      
      // Skroluj do kontakt sekcije da korisnik vidi success poruku
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          const headerOffset = 100;
          const elementPosition = contactSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
      
// Resetuj formu ALI ZADRŽI discount informacije
      const currentDiscountCode = discountCode;
      const currentDiscountInfo = discountInfo;
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        birthDate: '',
        birthTime: '',
        birthPlace: '',
        message: '',
        maritalStatus: '',
        employmentStatus: '',
        partnerFirstName: '',
        partnerLastName: '',
        partnerBirthDate: '',
        partnerBirthTime: '',
        partnerBirthPlace: '',
        question1: '',
        question2: '',
        question3: '',
        question4: '',
        question5: '',
        question6: '',
        question7: '',
        question8: '',
        question9: '',
        question10: ''
      });
      
      // Vrati discount informacije
      setDiscountCode(currentDiscountCode);
      setDiscountInfo(currentDiscountInfo);

    } catch (err: any) {
      console.error('Greška:', err);
      setError(err.message || 'Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Određivanje broja pitanja na osnovu odabrane usluge
  const getNumberOfQuestions = () => {
    if (formData.service === 'three-questions') return 3;
    if (formData.service === 'five-questions') return 5;
    if (formData.service === 'ten-questions') return 10;
    return 0;
  };

  const numberOfQuestions = getNumberOfQuestions();
  
// Da li je odabrana Sinastija
  const isSynastry = formData.service === 'synastry';

  return (
    <>
      {/* Success Message - Prikazuje se umesto forme */}
      {showSuccess && paymentInfo ? (
        <Card className="border-green-500/20 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
              <CardTitle className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Porudžbina Uspešno Poslata!</CardTitle>
            </div>
            <CardDescription className="text-base">
              Hvala vam! Primili smo vašu porudžbinu i poslali potvrdu na vaš email.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Podaci za Uplatu */}
            <div className="bg-primary/5 p-6 rounded-lg border border-primary/20">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                💳 Podaci za Uplatu
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Primalac:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{paymentInfo.recipient}</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard(paymentInfo.recipient)}
                      className="h-6 w-6 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Broj Računa:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{paymentInfo.accountNumber}</span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => copyToClipboard(paymentInfo.accountNumber)}
                      className="h-6 w-6 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Iznos:</span>
                  <span className="font-bold text-lg text-primary">{paymentInfo.amount}</span>
                </div>
              </div>
            </div>

            {/* Instrukcije za Uplatu */}
            <div className="bg-blue-50 dark:bg-blue-950/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span>💳</span>
                Kako Izvršiti Uplatu?
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 dark:text-blue-400">1.</span>
                  <div>
                    <p className="font-medium">Otvorite vašu bankarsku aplikaciju</p>
                    <p className="text-muted-foreground">Ulogujte se u mobilno bankarstvo</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 dark:text-blue-400">2.</span>
                  <div>
                    <p className="font-medium">Izaberite "Nova Uplata" ili "Plaćanje"</p>
                    <p className="text-muted-foreground">Unesite gore navedene podatke (primalac, račun, iznos)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 dark:text-blue-400">3.</span>
                  <div>
                    <p className="font-medium">Potvrdite uplatu</p>
                    <p className="text-muted-foreground">Snimite ili sačuvajte potvrdu o uplati</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-blue-600 dark:text-blue-400">4.</span>
                  <div>
                    <p className="font-medium">Pošaljite potvrdu</p>
                    <p className="text-muted-foreground">Kontaktirajte nas putem WhatsApp-a ili email-a</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sledeći Koraci */}
            <div className="bg-green-50 dark:bg-green-950/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span>✨</span>
                Sledeći Koraci
              </h3>
              <ol className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-green-600 dark:text-green-400">1.</span>
                  <span>Izvršite uplatu na gore navedeni račun</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-green-600 dark:text-green-400">2.</span>
                  <span>Pošaljite potvrdu uplate na WhatsApp ili email</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-green-600 dark:text-green-400">3.</span>
                  <span>Vaša analiza će biti gotova u roku od 5 dana</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-green-600 dark:text-green-400">4.</span>
                  <span>Dobićete PDF dokument na vaš email</span>
                </li>
              </ol>
            </div>
            {/* Kontakt Dugmad */}
            <div className="flex gap-3">
              <Button 
                className="flex-1" 
                onClick={() => window.open('https://wa.me/381653471080', '_blank')}
              >
                <span className="mr-2">💬</span>
                WhatsApp
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => window.location.href = 'mailto:astroariesstudio@gmail.com'}
              >
                <span className="mr-2">✉️</span>
                Email
              </Button>
            </div>

            {/* Dugme za novu porudžbinu */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setShowSuccess(false);
                setPaymentInfo(null);
              }}
            >
              ← Nova Porudžbina
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-primary/10">
        <CardHeader>
          <CardTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Poručite Analizu</CardTitle>
          <CardDescription>
            Popunite formu sa vašim podacima i odaberite željenu uslugu
          </CardDescription>
        </CardHeader>
        <CardContent>
          {wheelCode && (
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-2 border-green-500/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Gift className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div className="flex-1">
                  <p className="font-semibold text-green-800 dark:text-green-300">🎰 Vaš kod sa Točka Sreće:</p>
                  <p className="text-lg font-mono font-bold text-green-900 dark:text-green-200">{wheelCode}</p>
                </div>
                {discountPct > 0 && (
                  <div className="bg-green-600 text-white px-4 py-2 rounded-full font-bold text-lg">
                    -{discountPct}%
                  </div>
                )}
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {/* Sekcija: Osnovni Podaci */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <span className="text-2xl">👤</span>
                <h3 className="font-semibold text-lg">Osnovni Podaci</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Ime <span className="text-destructive">*</span></label>
                <Input 
                  placeholder="Vaše ime" 
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Prezime <span className="text-destructive">*</span></label>
                <Input 
                  placeholder="Vaše prezime" 
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>
</div>
              
              <div className="space-y-2">
              <label className="text-sm font-medium">Email <span className="text-destructive">*</span></label>
              <Input 
                type="email" 
                placeholder="vas@email.com" 
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
</div>
              
              <div className="space-y-2">
              <label className="text-sm font-medium">Telefon / WhatsApp</label>
              <Input 
                type="tel" 
                placeholder="+381 60 123 4567" 
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
</div>
            </div>

            {/* Sekcija: Odabir Usluge */}
            <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🔮</span>
                <h3 className="font-semibold text-lg">Odabir Usluge</h3>
              </div>
              
              <div className="space-y-2">
              <label className="text-sm font-medium">Odaberite Uslugu <span className="text-destructive">*</span></label>
              {/* Native select za bolje performanse na mobilnom */}
              <select
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="" disabled>Izaberite analizu...</option>
                {SERVICES.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.title} - {service.price}
                  </option>
                ))}
              </select>
</div>
            </div>

            {/* Sekcija: Podaci o Rođenju */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <span className="text-2xl">🎂</span>
                <h3 className="font-semibold text-lg">Podaci o Rođenju</h3>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Datum Rođenja <span className="text-destructive">*</span></label>
                <Input 
                  type="date" 
                  value={formData.birthDate}
                  onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Vreme <span className="text-destructive">*</span></label>
                <Input 
                  type="time" 
                  placeholder="14:30" 
                  value={formData.birthTime}
                  onChange={(e) => setFormData({...formData, birthTime: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Mesto <span className="text-destructive">*</span></label>
                <Input 
                  placeholder="Beograd" 
                  value={formData.birthPlace}
                  onChange={(e) => setFormData({...formData, birthPlace: e.target.value})}
                  required
                />
</div>
</div>
            </div>

            {/* Sekcija: Lični Podaci */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <span className="text-2xl">❤️</span>
                <h3 className="font-semibold text-lg">Lični Podaci</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Bračni/Emotivni Status <span className="text-destructive">*</span></label>
                <select
                  value={formData.maritalStatus}
                  onChange={(e) => setFormData({...formData, maritalStatus: e.target.value})}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="" disabled>Izaberite status...</option>
                  <option value="samac">Samac/Samica</option>
                  <option value="veza">U vezi</option>
                  <option value="vereni">Vereni</option>
                  <option value="brak">U braku</option>
                  <option value="razveden">Razveden/a</option>
                  <option value="udovac">Udovac/Udovica</option>
                  <option value="komplikovano">Komplikovano</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Zaposlenje/Poslovni Status <span className="text-destructive">*</span></label>
                <select
                  value={formData.employmentStatus}
                  onChange={(e) => setFormData({...formData, employmentStatus: e.target.value})}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="" disabled>Izaberite status...</option>
                  <option value="zaposlen">Zaposlen/a</option>
                  <option value="nezaposlen">Nezaposlen/a</option>
                  <option value="student">Student/kinja</option>
                  <option value="penzioner">Penzioner/ka</option>
                  <option value="preduzetnik">Preduzetnik/ca</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="domacica">Domaćica/Domaćin</option>
                  <option value="trazi_posao">Tražim posao</option>
                </select>
              </div>
</div>
            </div>

            {/* Polja za partnera - prikazuju se samo za Sinastiju */}
            {isSynastry && (
              <div className="space-y-4 p-4 bg-pink-50 dark:bg-pink-950/20 rounded-lg border border-pink-200 dark:border-pink-800">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <span className="text-pink-600 dark:text-pink-400">❤️</span>
                  Podaci o Partneru
                </h3>
                <p className="text-sm text-muted-foreground">
                  Za sinastiju (uporedni horoskop) potrebni su podaci o oba partnera.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Ime Partnera <span className="text-destructive">*</span></label>
                    <Input 
                      placeholder="Ime partnera" 
                      value={formData.partnerFirstName}
                      onChange={(e) => setFormData({...formData, partnerFirstName: e.target.value})}
                      required={isSynastry}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Prezime Partnera <span className="text-destructive">*</span></label>
                    <Input 
                      placeholder="Prezime partnera" 
                      value={formData.partnerLastName}
                      onChange={(e) => setFormData({...formData, partnerLastName: e.target.value})}
                      required={isSynastry}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Datum Rođenja <span className="text-destructive">*</span></label>
                    <Input 
                      type="date" 
                      value={formData.partnerBirthDate}
                      onChange={(e) => setFormData({...formData, partnerBirthDate: e.target.value})}
                      required={isSynastry}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Vreme <span className="text-destructive">*</span></label>
                    <Input 
                      type="time" 
                      placeholder="14:30" 
                      value={formData.partnerBirthTime}
                      onChange={(e) => setFormData({...formData, partnerBirthTime: e.target.value})}
                      required={isSynastry}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Mesto <span className="text-destructive">*</span></label>
                    <Input 
                      placeholder="Beograd" 
                      value={formData.partnerBirthPlace}
                      onChange={(e) => setFormData({...formData, partnerBirthPlace: e.target.value})}
                      required={isSynastry}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Polja za pitanja - prikazuju se samo za usluge sa pitanjima */}
            {numberOfQuestions > 0 && (
              <div className="space-y-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <span className="text-accent">❓</span>
                  Vaša Pitanja ({numberOfQuestions})
                </h3>
                <p className="text-sm text-muted-foreground">
                  Unesite vaša konkretna pitanja. Budite što precizniji kako bismo vam dali najtačnije odgovore.
                </p>
                {Array.from({ length: numberOfQuestions }, (_, i) => (
                  <div key={i} className="space-y-2">
                    <label className="text-sm font-medium">
                      Pitanje {i + 1} <span className="text-destructive">*</span>
                    </label>
                    <Textarea 
                      placeholder={`Unesite vaše ${i + 1}. pitanje...`}
                      rows={2}
                      value={formData[`question${i + 1}` as keyof typeof formData]}
                      onChange={(e) => setFormData({...formData, [`question${i + 1}`]: e.target.value})}
                      required
                    />
                  </div>
                ))}
              </div>
            )}

{/* Dodatne informacije - samo za usluge BEZ pitanja */}
            {numberOfQuestions === 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <span className="text-2xl">📝</span>
                  <h3 className="font-semibold text-lg">Dodatne Informacije</h3>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Šta želite da analiziramo posebno za vas?</label>
                  <Textarea 
                    placeholder="Npr: Fokus na karijeru, ljubavni život, finansije, zdravlje..."
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Discount Kod Sekcija */}
            <div className="space-y-4 p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg border-2 border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                  Imate Discount Kod?
                </h3>
              </div>
              
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Unesite kod (npr: WHEEL-ABC123)"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => validateDiscountCode(discountCode)}
                  disabled={isValidatingCode || !discountCode}
                >
                  {isValidatingCode ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Primeni'
                  )}
                </Button>
              </div>

              {discountInfo && (
                <Alert className={discountInfo.valid ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700' : 'bg-red-100 border-red-300 dark:bg-red-900/30 dark:border-red-700'}>
                  <Sparkles className={`w-4 h-4 ${discountInfo.valid ? 'text-green-600' : 'text-red-600'}`} />
                  <AlertDescription className={discountInfo.valid ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}>
                    {discountInfo.message}
                    {discountInfo.valid && (
                      <div className="mt-2 text-2xl font-bold text-green-600 dark:text-green-400">
                        {discountInfo.discount === 'FREE' ? '🎁 BESPLATNO' : `💰 ${discountInfo.discount} POPUSTA`}
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <p className="text-xs text-muted-foreground text-center">
                💡 Zavrtite Slot Mašinu da osvojite popust!
              </p>
            </div>

            {/* Submit Dugme */}
            <div className="pt-4 border-t">
              <Button className="w-full" size="lg" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Šaljem...
                </>
              ) : (
                <>
                  Pošalji Porudžbinu
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
</Button>
<p className="text-xs text-muted-foreground text-center mt-3">
                <span className="text-destructive">*</span> Obavezna polja
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
      )}
    </>
  );
}